import React from 'react';
import styled from 'styled-components';
import CaseStudyTemplate, {
  CaseStudySection,
  CaseStudyVideo,
  StoryParagraph,
} from './CaseStudyTemplate';

const CodeBlock = styled.pre`
  margin: 0 0 1rem;
  padding: 1rem 1.1rem;
  border-radius: 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  overflow-x: auto;
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 16px;
  line-height: 1.5;
  color: var(--text);
  tab-size: 2;
`;

const StorySubheading = styled.h3`
  margin: 1.35rem 0 0.65rem;
  font-family: var(--font-serif);
  font-size: 18px;
  font-weight: 500;
  color: var(--text);
  letter-spacing: -0.02em;
  line-height: 1.35;

  &:first-child {
    margin-top: 0;
  }
`;

const LayerTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 0 0 1.25rem;
  font-size: 18px;
  color: var(--text-muted);
  line-height: 1.55;

  th,
  td {
    border: 1px solid var(--border);
    padding: 0.65rem 0.85rem;
    text-align: left;
    vertical-align: top;
  }

  th {
    background: var(--surface);
    font-weight: 500;
    color: var(--text);
    font-size: 16px;
  }

  td code {
    font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
    font-size: 0.88em;
  }
`;

/** Block spans — intro lives inside CaseStudyTemplate's <p>, so we avoid nested <p> */
const IntroLine = styled.span`
  display: block;
  margin: 0 0 1rem;

  &:last-child {
    margin-bottom: 0;
  }

  code {
    font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
    font-size: 0.92em;
  }
`;

const PomodoroCaseStudy = () => (
  <CaseStudyTemplate
    title="Designing a pomodoro timer with a personal touch"
    intro={
      <IntroLine>
        Most Pomodoro apps are minimal, cold, or just a tomato timer. My sister wanted something better than that, so I built her a personal website that is a combination of a pomodoro timer, task manager, her music, and a photo carousel.
      </IntroLine>
    }
    details={[
      { label: 'Type', value: 'Personal project' },
      { label: 'Stack', value: 'React, JavaScript, HTML/CSS' },
      { label: 'Role', value: 'Designer, Developer' },
    ]}
    backTarget="selected-work"
    afterIntro={
      <CaseStudyVideo
        src="/leah.mp4"
        caption="Screen recording of the Pomodoro workspace: timer, gradients, Spotify embed, and the vintage-camera photo carousel."
        ariaLabel="Pomodoro timer app demo for Leah"
        flushTop
      />
    }
  >
    <CaseStudySection id="camera" title="The camera widget">
      <StoryParagraph>
        My favorite feature of this website is the photo carousel styled as a vintage camera. The idea was to create a workspace that cycles through her own photos while she sets up her session (familiar faces, places she likes) so the app feels like a desk rather than a productivity tool.
      </StoryParagraph>
      <CaseStudyVideo
        src="/pomodoro-camera-widget.mov"
        caption="Screen recording: vintage camera chrome, viewfinder photos, and prev/next controls in the setup view."
        ariaLabel="Camera widget carousel — browsing photos in the viewfinder"
      />
      <StoryParagraph>
        The widget only appears during setup. Once the timer starts, the entire right column, camera included, hides. It&apos;s a deliberate split: personal and ambient when you&apos;re getting ready, invisible when you need to focus.
      </StoryParagraph>
      <CodeBlock>{`<div className={\`right-section \${isRunning ? 'hidden' : ''}\`}>
  <ScheduleWidget />
</div>`}</CodeBlock>
    </CaseStudySection>

    <CaseStudySection id="how-it-works" title="How it works">
      <StorySubheading>Data: a list of images and one index</StorySubheading>
      <StoryParagraph>
        All eight photos are imported at build time. Webpack resolves the file paths into URLs during the CRA build, so the array is just a list of strings by the time React sees it:
      </StoryParagraph>
      <CodeBlock>{`const photos = [photo1, photo2, photo3, photo4, photo5, photo6, photo7, photo8];`}</CodeBlock>
      <StoryParagraph>
        React only needs to know one thing: which slot you&apos;re on. <code>currentPhotoIndex</code> is an integer from 0 to 7. Changing it triggers a re-render, which updates the <code>src</code> on the image inside the overlay.
      </StoryParagraph>

      <StorySubheading>Behavior: wraparound navigation</StorySubheading>
      <StoryParagraph>
        Both nav functions use the functional form of <code>setState</code> — deriving the next index from the previous value rather than from a closure over the current one. That keeps updates safe even if React batches them:
      </StoryParagraph>
      <CodeBlock>{`const nextPhoto = () => {
  setCurrentPhotoIndex((prevIndex) =>
    prevIndex === photos.length - 1 ? 0 : prevIndex + 1
  );
};

const prevPhoto = () => {
  setCurrentPhotoIndex((prevIndex) =>
    prevIndex === 0 ? photos.length - 1 : prevIndex - 1
  );
};`}</CodeBlock>
      <StoryParagraph>
        After photo 8, next wraps to photo 1. Before photo 1, previous wraps to photo 8. The reel never dead-ends.
      </StoryParagraph>

      <StorySubheading>UI: three layers, one illusion</StorySubheading>
      <StoryParagraph>
        The camera feel comes entirely from CSS stacking — not JavaScript. Inside <code>.camera-container</code> (which is <code>position: relative</code>), three layers sit on top of each other:
      </StoryParagraph>
      <LayerTable>
        <thead>
          <tr>
            <th scope="col">Layer</th>
            <th scope="col">What it is</th>
            <th scope="col">Role</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Bottom</td>
            <td>
              <code>camera.svg</code>
            </td>
            <td>Decorative chrome; defines the &quot;device&quot;</td>
          </tr>
          <tr>
            <td>Middle</td>
            <td>
              <code>.photo-overlay</code> + image
            </td>
            <td>
              The &quot;LCD screen&quot; — shows <code>photos[currentPhotoIndex]</code>
            </td>
          </tr>
          <tr>
            <td>Top</td>
            <td>
              <code>.nav-btn-overlay</code> images
            </td>
            <td>
              Hit targets; <code>onClick</code> fires <code>prevPhoto</code> / <code>nextPhoto</code>
            </td>
          </tr>
        </tbody>
      </LayerTable>
      <StoryParagraph>
        The photo overlay is absolutely positioned to sit exactly where the camera artwork suggests the screen would be. Nav controls sit above it at <code>z-index: 10</code> so clicks register on the controls, not on the photo underneath:
      </StoryParagraph>
      <CodeBlock>{`.photo-overlay {
  position: absolute;
  top: 40%;
  left: 37%;
  transform: translate(-50%, -50%);
  width: 260px;
  height: 185px;
  overflow: hidden;
  border-radius: 2px;
  z-index: 5;
}

.nav-btn-overlay {
  position: absolute;
  cursor: pointer;
  z-index: 10;
  transition: transform 0.2s ease;
}`}</CodeBlock>

      <StorySubheading>Events: image as button</StorySubheading>
      <StoryParagraph>
        The controls are <code>&lt;img&gt;</code> elements with <code>onClick</code> handlers — no <code>&lt;button&gt;</code> wrapper. The browser fires click on any visible element, so this works. The honest tradeoff: it&apos;s simple and readable, but <code>&lt;button&gt;</code> elements or explicit <code>role=&quot;button&quot;</code> with keyboard handlers would be the accessible choice. For a personal app used by one person on one device, the tradeoff was acceptable — it&apos;s worth naming if this pattern were to be reused.
      </StoryParagraph>
    </CaseStudySection>

    <CaseStudySection id="metaphor" title="Next steps">
      <StoryParagraph>
        My sister uses this every day for her work which is a great feeling. The next step is to create ways for her to customize the website to her liking like changing the photos, swapping between spotify playlists, and adding a custom background image. 
      </StoryParagraph>
    </CaseStudySection>
  </CaseStudyTemplate>
);

export default PomodoroCaseStudy;
